import {
  AfterViewInit,
  Component,
  DestroyRef,
  ErrorHandler,
  OnInit,
  inject,
  viewChild,
  Signal,
  DOCUMENT,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { isSentryErrorHandler } from '@techniek-team/sentry-web';
import {
  BehaviorSubject,
  firstValueFrom,
  from,
  Observable,
  ReplaySubject,
} from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { AlfredClientConfig } from '../../../../alfred-client.config';
import { AlfredClientService } from '../../../../alfred-client.service';
import { GapMatchQuestionAnswer } from '../../../../models/answer/gap-match-answer.model';
import { GapMatchQuestion } from '../../../../models/questions/gap-match-question.model';
import {
  DynamicComponentLoader,
  OnLoaded,
} from '@lyceo/shared/dynamic-component-loading/feature-main';
import { InvalidAnswerTypeError } from '../../../../shared/errors/invalid-answer-type-error';
import {
  QuestionAnswer,
  QuestionControlType,
} from '../question-type.interface';
import { GapTextPlaceholderDirective } from './gap-text-placeholder.directive';
import { GapComponent } from './gap/gap.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import {
  DisplayMathPipe,
  QuestionTableDataContainer,
  QuestionTableService,
  QuestionTableComponent,
  QuestionPlaceholderDirective,
} from '@lyceo/shared/question-display/feature-main';

/**
 * The question type that contains a text with gaps. The user should fill
 * in the correct answers to the gaps.
 *
 * <lib-gap-match-question
 *                         [formControl]="questionFormCtrl">
 * </lib-gap-match-question>
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-gap-match-question',
  templateUrl: './gap-match-question.component.html',
  styleUrls: ['./gap-match-question.component.scss'],
  imports: [GapTextPlaceholderDirective, AsyncPipe, DisplayMathPipe],
})
export class GapMatchQuestionComponent
  implements OnInit, AfterViewInit, OnLoaded, QuestionControlType
{
  private readonly formBuilder: UntypedFormBuilder = inject(UntypedFormBuilder);
  private readonly errorHandler: ErrorHandler = inject(ErrorHandler);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly document: Document = inject<Document>(DOCUMENT);
  public config: AlfredClientConfig = inject(AlfredClientConfig);
  public dynamicComponentLoader: DynamicComponentLoader = inject(
    DynamicComponentLoader,
  );
  public alfredClientService: AlfredClientService = inject(AlfredClientService);
  public questionTableService: QuestionTableService =
    inject(QuestionTableService);

  /**
   * Placeholder directive in which the html from the server is injected.
   */
  public readonly gapTextPlaceholder: Signal<GapTextPlaceholderDirective> =
    viewChild.required(GapTextPlaceholderDirective);

  /**
   * The gap match question that is currently shown to the user.
   */
  public set question(question: GapMatchQuestion) {
    if (!question.value && this.alfredClientService.isDirectHandleMode()) {
      question.value = '<i>[hier komt de vraag]</i>';
    }

    // Create tables from the question value if available.
    const tableContainer: QuestionTableDataContainer =
      this.questionTableService.tablesJsonToQuestionTableData(question.value);
    tableContainer.questionWithTablePlaceholders =
      tableContainer.questionWithTablePlaceholders.replace(
        /%FIELD%/g,
        '<span class="gap"></span>',
      );

    this.questionTableDataContainer = tableContainer;

    this._question$.next({
      ...question,
      value: this.questionTableDataContainer.questionWithTablePlaceholders,
    });
  }

  /**
   * questions data used to created the gap match question.
   * private Used in inner html template.
   */
  public _question$: BehaviorSubject<GapMatchQuestion> =
    new BehaviorSubject<GapMatchQuestion>(undefined as never);

  /**
   * Input to prefill the answer of the question.
   */
  public set answer(answer: GapMatchQuestionAnswer) {
    if (!answer) {
      return;
    }

    if (Array.isArray(answer)) {
      this.answers$.next(this.normalize(answer));
      return;
    }

    // This form control only accepts an object (array) or null.
    // Other enums are ignored and a silent error is send to Sentry
    if (!this.config.production) {
      throw new InvalidAnswerTypeError(answer, 'string[]');
    }

    if (isSentryErrorHandler(this.errorHandler)) {
      const error: InvalidAnswerTypeError = new InvalidAnswerTypeError(
        answer,
        'string[]',
      );
      this.errorHandler.captureError(error);
    }
  }

  public get answer(): GapMatchQuestionAnswer {
    return this.getValue() as GapMatchQuestionAnswer;
  }

  private readonly answers$: BehaviorSubject<GapMatchQuestionAnswer> =
    new BehaviorSubject<GapMatchQuestionAnswer>([]);

  /**
   * The form array containing all the gaps.
   * private Used in inner html template.
   */
  public _gapMatchForm: UntypedFormArray = this.buildGapMatchFormArray();

  /**
   * Subject which starts to emit when the view is rendered. In this case it needed
   * because of the innerHtml of which the rendering takes place asynchronously of the
   * parents {@see GapMatchQuestionComponent.ngAfterViewInit} With this we can tell the parent
   * that the compleet component in loaded.
   */
  public onFinishedInit$: ReplaySubject<void> = new ReplaySubject<void>();

  /**
   * Data for tables used in the question.
   */
  private questionTableDataContainer!: QuestionTableDataContainer;

  /**
   * The question placeholder will be used to place the correct question
   * specific component inside by dynamic component loading.
   */
  private readonly questionPlaceholder: Signal<QuestionPlaceholderDirective> =
    viewChild.required(QuestionPlaceholderDirective);

  /**
   * @inheritDoc
   */
  public ngOnInit(): void {
    this.createAnswerSubscriber();
  }

  /**
   * @inheritDoc
   */
  public ngAfterViewInit(): void {
    this.gapTextPlaceholder()
      .ready()
      .then(async () => {
        // Render tables first, since they can hold gap questions as well!
        await this.injectQuestionTables();
        // Once done, inject the gaps.
        await this.injectGapFields();

        this.onFinishedInit$.next();
        this.onFinishedInit$.complete();
      });
  }

  /**
   * @inheritDoc
   */
  public ready(): Promise<void> {
    return firstValueFrom(this.onFinishedInit$.pipe(take(1)));
  }

  /**
   * @inheritDoc
   */
  public isPristine(): boolean {
    return this._gapMatchForm.pristine;
  }

  /**
   * @inheritDoc
   */
  public getValue(): QuestionAnswer {
    return this.normalize(this._gapMatchForm.value) as QuestionAnswer;
  }

  /**
   * @inheritDoc
   */
  public onValueChanges(): Observable<QuestionAnswer> {
    return this._gapMatchForm.valueChanges.pipe(
      map((value) => {
        return this.normalize(value) as QuestionAnswer;
      }),
    );
  }

  /**
   * @inheritDoc
   */
  public isValid(): boolean {
    return this._gapMatchForm.valid;
  }

  /**
   * @inheritDoc
   */
  public onValidChanges(): Observable<boolean> {
    return this._gapMatchForm.statusChanges.pipe(
      filter((status) => status === 'VALID' || status === 'INVALID'),
      map((status) => status === 'VALID'),
    );
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this._gapMatchForm.disable();
    } else {
      this._gapMatchForm.enable();
    }
  }

  public getFocusElement(): Element | null {
    return this.document.querySelectorAll('lib-gap label input')[0];
  }

  /**
   * Normalize the return value. (trims the string of each field)
   */
  private normalize(value: GapMatchQuestionAnswer): GapMatchQuestionAnswer {
    return value.map((field) => {
      if (field === null) {
        return '';
      }
      if (typeof field === 'string') {
        return field.trim();
      }
      return field;
    });
  }

  /**
   * Replaces the gap tags with newly Injected GapComponents.
   */
  private injectGapFields(): Promise<void[]> {
    const instances: GapComponent[] =
      this.dynamicComponentLoader.injectReplacementComponentByElement<GapComponent>(
        this.gapTextPlaceholder().viewContainerRef,
        GapComponent,
        '.gap',
      );

    for (const instance of instances) {
      this._gapMatchForm.controls.push(instance.gapControl);
      this._gapMatchForm['_registerControl'](instance.gapControl);
    }

    if (this.alfredClientService.isApiMode()) {
      // set focus to first input
      instances[0].setFocus();
    }

    return Promise.all(instances.map((instance) => instance.ready()));
  }

  /**
   * Replaces the table placeholders with newly Injected QuestionTableComponents.
   */
  private injectQuestionTables(): Promise<void[]> {
    const instances: QuestionTableComponent[] =
      this.dynamicComponentLoader.injectReplacementComponentByElement<QuestionTableComponent>(
        this.gapTextPlaceholder().viewContainerRef,
        QuestionTableComponent,
        '.question-table-placeholder',
      );

    for (const [index, instance] of instances.entries()) {
      if (instance instanceof QuestionTableComponent) {
        instance.setData(
          this.questionTableDataContainer.questionTableData[index],
        );
      }
    }

    return Promise.all(instances.map((instance) => instance.ready()));
  }

  /**
   * Build the FormArray.
   */
  private buildGapMatchFormArray(): UntypedFormArray {
    return this.formBuilder.array([], this.hasAtLeastOneFilledInGap as never);
  }

  /**
   * Validator used for the gap FormArray.
   * This will check if there is at least a single gap filled in with an answer.
   */
  private hasAtLeastOneFilledInGap(
    control: UntypedFormArray,
  ): ValidationErrors | null {
    const validControls: AbstractControl[] = control.controls.filter(
      (item: AbstractControl) => {
        return Validators.required(item) === null;
      },
    );

    return validControls.length < 1
      ? { needAtLeastOneValidOption: true }
      : null;
  }

  /**
   * Waits for component to be ready after which it starts listing to the _answer$
   * subject for changes.
   */
  private createAnswerSubscriber(): void {
    from(this.ready())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.answers$.asObservable()),
        filter((answer) => !!answer),
      )
      .subscribe((answer) => {
        if (answer.length === 0) {
          this._gapMatchForm.reset();
        }
        if (answer.length === this._gapMatchForm.length) {
          this._gapMatchForm.setValue(answer);
        }
      });
  }
}
